import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { ApiError } from '../../lib/api';
import { Spinner } from '../../components/Spinner';

/** A ticking clock — this console is about what is happening right now. */
function Clock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);
  return (
    <div className="tabular text-rail-ink">
      <div className="text-[2.75rem] font-light leading-none tracking-[-0.02em]">
        {now.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
        <span className="ml-1 text-xl text-rail-ink-2">
          {now.toLocaleTimeString(undefined, { second: '2-digit' })}
        </span>
      </div>
      <div className="mt-2 text-[0.8125rem] text-rail-ink-2">
        {now.toLocaleDateString(undefined, {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
        })}
        {' · '}
        {Intl.DateTimeFormat().resolvedOptions().timeZone}
      </div>
    </div>
  );
}

export function LoginPage() {
  const { login, user, ready } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  if (ready && user) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from && from !== '/login' ? from : '/'} replace />;
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      const signedIn = await login(email.trim(), password);
      if (signedIn.role !== 'admin') {
        // The API would let them in; this console would be empty for them.
        setError(
          'This console is for administrators. Your account is a ' +
            `${signedIn.role}, so there is nothing here for you to manage.`,
        );
        setPending(false);
        return;
      }
      navigate('/', { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'Could not reach the API. Check that it is running and that VITE_API_URL points at it.',
      );
      setPending(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1fr_28rem]">
      {/* The board: identity, the clock, and what this console is for. */}
      <div className="hidden flex-col justify-between bg-rail p-10 lg:flex">
        <div className="flex items-center gap-2.5 text-rail-ink">
          <span
            aria-hidden="true"
            className="grid size-8 place-items-center rounded bg-rail-hover"
          >
            🎓
          </span>
          <span className="text-sm font-semibold">Tutoring Platform</span>
        </div>

        <div>
          <Clock />
          <p className="mt-8 max-w-sm text-[0.9375rem] leading-relaxed text-rail-ink-2">
            The operations console for live tutoring: who is online, which lessons
            are running, and everything support needs to put a booking right.
          </p>
        </div>

        <p className="text-[0.6875rem] text-rail-ink-2">
          Actions taken here are recorded against your name.
        </p>
      </div>

      <div className="flex items-center justify-center bg-paper px-6 py-12">
        <form onSubmit={onSubmit} className="w-full max-w-sm">
          <h1 className="text-xl font-semibold tracking-[-0.01em] text-ink">Sign in</h1>
          <p className="mt-1 text-[0.8125rem] text-ink-3">
            Use your administrator account.
          </p>

          <div className="mt-6 space-y-4">
            <label className="block">
              <span className="label">Email</span>
              <input
                className="input"
                type="email"
                autoComplete="username"
                required
                // biome-ignore lint/a11y/noAutofocus: the sign-in form is the entire page, so focusing it steals focus from nothing
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
              />
            </label>

            <label className="block">
              <span className="label">Password</span>
              <input
                className="input"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
          </div>

          {error && (
            <p
              role="alert"
              className="mt-4 rounded-md border border-danger/30 bg-danger-wash px-3 py-2 text-xs text-ink"
            >
              {error}
            </p>
          )}

          <button type="submit" className="btn btn-primary mt-5 w-full" disabled={pending}>
            {pending && <Spinner />}
            {pending ? 'Signing in…' : 'Sign in'}
          </button>

          <p className="mt-6 border-t border-rule pt-4 text-[0.6875rem] leading-relaxed text-ink-3">
            No administrator account yet? Create the first one from the API
            repository with{' '}
            <code className="mono text-ink-2">node scripts/create-admin.js</code>.
          </p>
        </form>
      </div>
    </div>
  );
}
